import { FC, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { trackEvent } from 'dibs-tracking';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { Button } from 'dibs-elements/exports/Button';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import styles from './SbSharedAuctionItemsBanner.scss';
import { SbSharedAuctionItemsBanner_auctionsItemSearch$key } from './__generated__/SbSharedAuctionItemsBanner_auctionsItemSearch.graphql';
import { SbSharedAuctionItemsBanner_seller$key } from './__generated__/SbSharedAuctionItemsBanner_seller.graphql';

type Props = {
    auctionsItemSearch: SbSharedAuctionItemsBanner_auctionsItemSearch$key;
    seller: SbSharedAuctionItemsBanner_seller$key;
    className?: string;
};

const SbSharedAuctionItemsBanner: FC<Props> = ({
    auctionsItemSearch: auctionsItemSearchRef,
    seller: sellerRef,
    className,
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    const auctionsItemSearch = useFragment(
        graphql`
            fragment SbSharedAuctionItemsBanner_auctionsItemSearch on ItemSearchQueryConnection {
                totalResults
            }
        `,
        auctionsItemSearchRef
    );
    const seller = useFragment(
        graphql`
            fragment SbSharedAuctionItemsBanner_seller on Seller {
                serviceId
                sellerProfile {
                    company
                }
            }
        `,
        sellerRef
    );

    const totalAuctions = auctionsItemSearch?.totalResults || 0;
    const sellerPk = seller?.serviceId;
    const shouldShow = totalAuctions >= 6 && sellerPk;

    if (!shouldShow) {
        return null;
    }

    const company = seller.sellerProfile?.company;
    const ctaLink = `/auctions/?sellerPk=${sellerPk}`;

    return (
        <div className={classnames(styles.container, className)} ref={containerRef}>
            <VisibilityTracker
                elementRef={containerRef}
                onVisibilityChange={({ isVisible }) =>
                    isVisible &&
                    trackEvent({
                        event: 'e_ga_event',
                        action: 'seller auction listings displayed',
                        category: 'promo interaction',
                        label: sellerPk,
                        noninteraction: 1,
                    })
                }
            />
            <HeadingLevel>
                {Heading => (
                    <Heading className={styles.header}>
                        <FormattedMessage
                            id="sb.SbSharedAuctionItemsBanner.header"
                            defaultMessage="{company} at Auction"
                            values={{
                                company: <span className={styles.italicHeader}>{company}</span>,
                            }}
                        />
                    </Heading>
                )}
            </HeadingLevel>
            <p className={styles.content}>
                <FormattedMessage
                    id="sb.SbSharedAuctionItemsBanner.content"
                    defaultMessage="<b>Looking for a rare item at a great deal?</b> Browse {company} auction items."
                    values={{
                        b: content => <span className={styles.bold}>{content}</span>,
                        company,
                    }}
                />
            </p>
            <div>
                <Button
                    type="secondary"
                    size="medium"
                    href={ctaLink}
                    onClick={() =>
                        trackEvent({
                            event: 'e_ga_event',
                            action: 'seller auction listings click',
                            category: 'promo interaction',
                            label: sellerPk,
                            noninteraction: 0,
                        })
                    }
                >
                    <FormattedMessage
                        id="sb.SbSharedAuctionItemsBanner.cta"
                        defaultMessage="Browse auction items"
                    />
                </Button>
            </div>
        </div>
    );
};

export default SbSharedAuctionItemsBanner;
